<template>
  <div class="h-100 d-flex flex-row">
    <div class="h-100 d-flex flex-column bg-white">
      <div class="h-100 flex-shrink-1 d-flex flex-column" style="min-height: 0; width: 28rem;">
        <ProgressBar v-if="loading" mode="indeterminate" />
        <div class="p-inputgroup py-3 px-2">
          <span class="p-float-label">
            <InputText v-model="contactsFilter" type="text" />
            <label><translate>Find Contact...</translate></label>
          </span>
        </div>
        <InfiniteList
          v-if="!hasNoContent"
          :get-all-items="contacts"
          id-property="originalId"
          class="contacts-list flex-shrink-1"
        >
          <template #element="props">
            <ContactListItem :contact="props.item" @select="handleContactSelect" @move="openMoveModal" />
          </template>
          <template #loading>
            <div class="w-100">
              <ProgressBar mode="indeterminate" />
            </div>
          </template>
        </InfiniteList>
        <div v-if="hasNoContent" class="d-flex flex-column h-100">
          <div v-if="!loading" class="d-flex flex-column justify-content-center h-100">
            <div class="text-center">
              <p class="h5 mb-2">
                <translate>No Contacts</translate>
              </p>
              <p><translate>Click "New" to create a contact.</translate></p>
            </div>
          </div>
          <div v-else class="text-center">
            <div class="pl-3 pr-3 pt-2 pb-4 d-flex">
              <Skeleton height="36px" class="flex-grow-1" />
            </div>
            <div class="pl-3 pr-3 pt-2 pb-4 d-flex">
              <Skeleton height="36px" class="flex-grow-1" />
            </div>
            <div class="pl-3 pr-3 pt-2 pb-4 d-flex">
              <Skeleton height="36px" class="flex-grow-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isOnMobile" class="flex-grow-1 h-100 separator-left" style="overflow-y: auto;">
      <ContactEditor
        v-if="selectedContact"
        :is-allowed-to-edit="isAllowedToEdit"
        :contact="selectedContact"
      />
      <div v-else class="d-flex flex-column justify-content-center h-100">
        <div class="text-center">
          <p class="h5 mb-2">
            <translate>No Contact selected</translate>
          </p>
          <p><translate>Click on a contact to edit it.</translate></p>
        </div>
      </div>
    </div>
    <div v-else>
      <Dialog
        v-model:visible="showModalOnMobile"
        position="right"
        :style="modalStyle"
        :draggable="false"
        :close-on-escape="true"
        :closable="true"
        @hide="handleReturnToAdressBook"
      >
        <ContactEditor
          v-if="selectedContact"
          :is-allowed-to-edit="isAllowedToEdit"
          :contact="selectedContact"
        />
      </Dialog>
    </div>
    <Dialog
      v-model:visible="showMoveModal"
      :header="i18n.$gettext('Move Contact')"
      :modal="true"
      @hide="contactToMove = null"
    >
      <div>
        <p><translate>Where to move the contact:</translate></p>
        <Listbox
          v-model="moveTarget"
          :options="availableAdressbookOptions"
          option-value="originalId"
          option-label="name"
        />
        <div class="row d-flex flex-row justify-content-center mt-4">
          <div class="col-auto">
            <Button
              icon="cil-plus"
              :label="i18n.$gettext('Move')"
              class=""
              :loading="moveLoading"
              @click="moveContact"
            />
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts">

import {Options, Vue} from "vue-class-component"
import {contactServiceApi} from "@/api/ContactServiceApi"
import Contact from "@/model/entry/Contact"
import AddressBook from "@/model/directory/AddressBook"
import InfiniteList from "@/components/common/InfiniteList.vue"
import ProgressBar from "primevue/progressbar"
import ContactListItem from "@/components/contacts/subcomponents/ContactListItem.vue"
import ContactEditor from "@/components/contacts/subcomponents/ContactEditor.vue"
import Button from "primevue/button"
import ContextMenu from "primevue/contextmenu"
import Skeleton from "primevue/skeleton"
import InputText from "primevue/inputtext"
import breakpointUtil from "@/util/BreakpointUtil"
import Dialog from "primevue/dialog"
import {Language, useGettext} from "@jshmrtn/vue3-gettext"
import {addressBookServiceApi} from "@/api/AddressBookServiceApi"
import RpcError from "@/api/RpcError"
import useToast from "@/util/toasts"
import Listbox from "primevue/listbox"
import {contactStore} from "@/store/ContactStore"
import Query from "@/model/common/Query"
import SortAndFilterUtil from "@/util/SortAndFilterUtil"


@Options({
  components: {
    Dialog, ContactEditor, ContactListItem, InfiniteList, ProgressBar, Button, ContextMenu, Skeleton, InputText, Listbox
  },
  //@ts-ignore
  props: {
    addressBook: [ AddressBook, Object ],
    searchQuery: {
      type: [ Query, Object ],
      default: null
    }
  },
  emits: []
})
export default class ContactsList extends Vue {

  i18n: Language = useGettext()
  toast = useToast()

  addressBook: AddressBook | null = null
  searchQuery!: Query | null
  loading = false
  contactsFilter: string =''
  showModalOnMobile: boolean  = false

  contactToMove: Contact | null = null
  moveTarget: string | null = null
  moveLoading = false

  get isOnMobile() {
    return breakpointUtil.isOnMobile()
  }

  get isAllowedToEdit() {
    return ['WRITE', 'OWNER'].includes(this.addressBook?.shareAccess || '')
  }

  get contacts(): Contact[] {
    let swr = null
    if (this.addressBook?.originalId && this.searchQuery) {
      swr = contactServiceApi.queryContacts(this.searchQuery)
      swr.call?.promise?.finally(() => {
        this.loading = false
      })
    } else if (this.addressBook?.originalId) {
      swr = contactServiceApi.getAllContacts(this.addressBook.originalId, 1000, [ 'structuredName.family:asc' ])
      swr.call?.promise?.then((ids: string[]) => {
        //Replace the state, to make sure no deleted contacts remain in it
        const contacts = [...contactStore.state.contacts.values()]
        contactStore.replaceContacts(contacts.filter(contact => (contact.originalParentId !== this.addressBook?.originalId) || ids.includes(contact.originalId || '')))
      }).finally(() => {
        this.loading = false
      })
    } else {
      return []
    }

    this.loading = swr.call?.loading || false
    if (swr.data) {
      swr.data.forEach(contact => (contact.originalParentId = this.addressBook?.originalId || contact.originalParentId))
    }
    let data = swr.data || []
    if (this.contactsFilter) {
      data = data.filter(c => {
        const lowerName: string | undefined = c.formattedName?.text?.toLowerCase()
        return lowerName ? lowerName.split(/\s+/).find(s => s.startsWith(this.contactsFilter.toLowerCase())) : false
      })
    }
    if (this.searchQuery) {
      SortAndFilterUtil.sort(data, [ 'structuredName.family:asc' ])
    }
    return data
  }

  get hasNoContent(): boolean {
    return this.contacts.length === 0
  }

  get selectedContact(){
    const selectedId = this.$route.params.contact as string
    if (!selectedId) return false
    return contactServiceApi.getContact(selectedId)
  }

  get hasSelectedContact(): boolean{
    return !!this.selectedContact
  }

  handleContactSelect(contact: Contact): void {
    if (this.isOnMobile) this.showModalOnMobile = true
    const newPath = "/contacts/" + this.addressBook?.originalId + "/" + contact.originalId
    void this.$router.push({path: newPath})
  }

  handleReturnToAdressBook(): void {
    const newPath = "/contacts/" + this.addressBook?.originalId
    void this.$router.push({path: newPath})
  }

  get modalStyle(){
    if (breakpointUtil.isOnMobile()) {
      return { width: "100%", margin: "0", height: "100% !important", maxHeight: "100%" }
    } else {
      return { width: "50%", margin: "1rem", height: "100%" }
    }
  }

  openMoveModal(contact: Contact | null) {
    this.contactToMove = contact
  }

  get showMoveModal(): boolean {
    return !!this.contactToMove
  }

  set showMoveModal(value: boolean) {
    if (!value) {
      this.moveTarget = null
      this.contactToMove = null
    }
  }

  get availableAdressbookOptions(): AddressBook[] {
    return  (addressBookServiceApi.getAddressBooks().data || []).filter(book => {
      return Boolean(book.originalId && book.name && book.originalId !== this.contactToMove?.originalParentId)
    })
  }

  moveContact() {
    console.dir(this.addressBook)
    console.dir(this.moveTarget)
    console.dir(this.contactToMove)
    if (this.addressBook?.originalId && this.moveTarget && this.contactToMove?.originalId) {
      this.moveLoading = true
      contactServiceApi._moveContact(this.contactToMove.originalId, this.moveTarget).then(() => {
        this.toast.success(this.i18n.$gettext("Contact moved"))
      }).catch((e: RpcError) => {
        this.toast.error(e.message, this.i18n.$gettext("Could not move contact"))
      }).finally(() => {
        this.moveLoading = false
        this.showMoveModal = false
      })
    }
  }
}
</script>

<style scoped lang="scss">

</style>
